import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useAuthContext } from 'contexts/AuthContext';
import Modal from './Modal';
import FilterablePoolSelect from './FilterablePoolSelect';
import FilterableCompanySelect from './FilterableCompanySelect';
import { getApiErrorMessage } from 'utils/apiErrors';
import { buildPumpSpecificationFields } from 'utils/pumpSpecs';
import { getSettablePumpStatusCodes } from './pumpStatusMeta';
import './EditPumpModal.scss'; // Use the same styles

export default function AddPumpModal({ isOpen, onClose, onSuccess, anchorRect = null }) {
  const { t } = useTranslation();
  const { pools } = useStoreState((state) => state.pools);
  const { companies } = useStoreState((state) => state.companies);
  const { createPump } = useStoreActions((actions) => actions.pumps);
  const { getCompanies } = useStoreActions((actions) => actions.companies);
  const { userRole, myUser } = useAuthContext();
  const [formData, setFormData] = useState({
    name: '',
    serial_number: '',
    model: '',
    lift_height: '',
    volume_capacity: '',
    current_pool_id: '',
    company_id: '',
    status: 1, // Default to "Varastossa"
    spyro_round_policy: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isKorjaaja = userRole === 3;
  const isKaivosKayttaja = userRole === 4;
  const isAdmin = Number(userRole) === 1;
  const profileCompanyId = useMemo(() => {
    const c = myUser?.company;
    if (c == null || c === '') return null;
    const n = Number(c);
    return Number.isFinite(n) ? n : null;
  }, [myUser?.company]);
  const companySelectionLocked = !isAdmin;
  const lockedCompanyDisplayName =
    myUser?.company_name ||
    companies.find((c) => c.id === profileCompanyId)?.name ||
    '';

  useEffect(() => {
    if (!isOpen) return;

    if (isAdmin && companies.length === 0) {
      getCompanies();
    }

    const defaultCompanyId = isAdmin ? '' : profileCompanyId ?? '';
    setFormData({
      name: '',
      serial_number: '',
      model: '',
      lift_height: '',
      volume_capacity: '',
      current_pool_id: '',
      company_id: defaultCompanyId,
      status: 1,
      spyro_round_policy: false,
    });
    setError(null);
  }, [isOpen, isAdmin, profileCompanyId, companies.length, getCompanies]);

  useEffect(() => {
    if (!isOpen || isAdmin || profileCompanyId == null) return;
    setFormData((prev) =>
      prev.company_id === profileCompanyId
        ? prev
        : { ...prev, company_id: profileCompanyId }
    );
  }, [isOpen, isAdmin, profileCompanyId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (companySelectionLocked) {
        if (profileCompanyId == null) {
          setError(t('pump_modal_company_missing_profile'));
          setSaving(false);
          return;
        }
      }

      const companyForApi = isAdmin
        ? formData.company_id === ''
          ? null
          : parseInt(formData.company_id, 10)
        : profileCompanyId;

      const submitData = {
        ...formData,
        current_pool_id: formData.current_pool_id === '' ? null : parseInt(formData.current_pool_id),
        company: companyForApi,
        ...buildPumpSpecificationFields({
          lift_height: formData.lift_height,
          volume_capacity: formData.volume_capacity,
        }),
      };
      // Remove company_id because we use the company field; omit capacity so legacy % is untouched
      delete submitData.company_id;
      
      // Use store action to create pump - updates store automatically
      const newPump = await createPump(submitData);
      
      if (onSuccess) {
        onSuccess(newPump);
      }
      onClose();
    } catch (err) {
      console.error('Error creating pump:', err);
      setError(getApiErrorMessage(err, t('pump_modal_create_error')));
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = useMemo(() => {
    // For pump creation, only operational statuses make sense (1–7);
    // 8 (POISTETTU ALTAASTA) and 9 (PALAUTUVIEN VARASTO) are state transitions.
    const addBaseCodes = [1, 2, 3, 4, 5, 6, 7];
    const settable = getSettablePumpStatusCodes(userRole);
    // Keep the role's own menu order (the repairer has a workflow-specific order).
    const codes = settable.filter((c) => addBaseCodes.includes(c));
    return codes.map((value) => ({
      value,
      label: t(`pump_status_${value}`),
    }));
  }, [userRole, t]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('pump_modal_add_title')} size="medium" anchorRect={anchorRect}>
        <form className="edit-modal-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('pump_modal_company')}</label>
            {companySelectionLocked ? (
              <input
                type="text"
                className="form-input"
                readOnly
                value={lockedCompanyDisplayName || t('pump_detail_unknown')}
                aria-label={t('pump_modal_company')}
                style={{
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  cursor: 'default',
                }}
              />
            ) : (
              <FilterableCompanySelect
                companies={companies}
                value={formData.company_id}
                onChange={(companyId) => handleInputChange('company_id', companyId)}
                placeholder={t('pump_modal_company_ph')}
                emptyOptionLabel={t('pump_modal_no_company')}
                disabled={isKorjaaja}
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_name')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('pump_modal_name_ph')}
              required
              disabled={isKorjaaja}
              style={isKorjaaja ? { 
                backgroundColor: '#f5f5f5', 
                color: '#666',
                cursor: 'not-allowed'
              } : {}}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_serial')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.serial_number}
              onChange={(e) => handleInputChange('serial_number', e.target.value)}
              placeholder="SN123456"
              required
              disabled={isKorjaaja}
              style={isKorjaaja ? { 
                backgroundColor: '#f5f5f5', 
                color: '#666',
                cursor: 'not-allowed'
              } : {}}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_model')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              placeholder={t('pump_modal_model_ph')}
              disabled={isKorjaaja}
              style={isKorjaaja ? { 
                backgroundColor: '#f5f5f5', 
                color: '#666',
                cursor: 'not-allowed'
              } : {}}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_lift_height')}</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              value={formData.lift_height}
              onChange={(e) => handleInputChange('lift_height', e.target.value)}
              placeholder={t('pump_modal_lift_height_ph')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_volume_capacity')}</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              value={formData.volume_capacity}
              onChange={(e) => handleInputChange('volume_capacity', e.target.value)}
              placeholder={t('pump_modal_volume_capacity_ph')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_pool')}</label>
            <FilterablePoolSelect
              pools={pools}
              value={formData.current_pool_id}
              onChange={(poolId) => handleInputChange('current_pool_id', poolId)}
              placeholder={t('filter_pool_default_ph')}
              emptyOptionLabel={t('pump_modal_pool_empty')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_status')}</label>
            <select
              className="form-select"
              value={formData.status}
              onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
              required
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={!!formData.spyro_round_policy}
                onChange={(e) => handleInputChange('spyro_round_policy', e.target.checked)}
                disabled={isKorjaaja || isKaivosKayttaja}
              />
              {t('pump_modal_spyro')}
            </label>
            <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: '4px' }}>
              {t('pump_modal_spyro_help')}
            </div>
          </div>

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={onClose}
              disabled={saving}
            >
              {t('cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={
                saving ||
                !formData.name ||
                !formData.serial_number ||
                (companySelectionLocked && profileCompanyId == null)
              }
            >
              {saving ? t('pump_modal_creating') : t('pump_modal_create_btn')}
            </button>
          </div>
        </form>
    </Modal>
  );
}

